/* eslint-disable no-console */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import toast from "react-hot-toast";
import { AgenciasDropdown, matchAgenciaFilter } from "../../../components/AgenciasDropdown";
import { CategoriaSubcategoriaFilter } from "../../../components/CategoriaSubcategoriaFilter";
import { Header } from "../../../components/Header";
import { Loader } from "../../../components/Loader";
import { getAniosCarga } from "../../../constants/anios";
import { AGENCIA_ADLAB_PERU } from "../../../constants/permisos";
import { ROLES } from "../../../constants/roles";
import { useAuthContext } from "../../../context/auth";
import { getImporteEnUSD, getRateForOpp, seleccionarOportunidadesPorEstatus } from "../../../utils/calculateVentas";
import { cargarOportunidadesVentas } from "../../../utils/cargarOportunidadesVentas";
import { esVentaEnPeriodoBroker } from "../../../utils/esVentaEnPeriodoBroker";
import { formatDate, getDowngradeInfo } from "../../../utils/oportunidadDateStatus";
import { updateContrato } from "../../../utils/updateContrato";
import { updateOportunidad } from "../../../utils/updateOportunidad";
import { updateOportunidadBTL } from "../../../utils/updateOportunidadBTL";
import { useCurrency } from "../../../utils/useCurrency";
import { useEmpresaProductoFilter } from "../../../utils/useEmpresaProductoFilter";
import { useExchangeRates } from "../../../utils/useExchangeRates";
import { useHubHeaderFilters } from "../HubHeaderContext";
import { useVentasDataSync } from "../VentasDataVersionContext";
import { ConfirmarCaidaModal } from "./ConfirmarCaidaModal";
import { ConfirmarCierre100Modal } from "./ConfirmarCierre100Modal";
import styles from "./Pizarra.module.scss";

const MESES_LETRAS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

// Columnas de izquierda a derecha, EXACTAMENTE en este orden (la pizarra física de Ignacio).
// El 0% no va (regla del negocio, no de UI — un pipeline sin proyección no es "pizarra").
const ESTATUS_COLUMNAS = ["50%", "75%", "90%", "100%"];

// Comparación insensible a mayúsculas/espacios — nunca contra el string crudo (2026-09-22, punto
// 3): evita repetir "TRANSBANK | TRANSBANK" cuando marca y empresa son la misma cosa con distinto
// casing/espaciado. Una sola fuente, reusada por el render de la celda Y por el title del tooltip.
const normalizarTexto = (valor) => (valor || "").trim().toLocaleUpperCase();

// Nombre completo del cliente para superficies FUERA de la tarjeta (el toast del modo Mover,
// 2026-09-22) — MISMO criterio que ya usa la tarjeta inline (empresa + " | " + marca solo si es
// distinta, normalizarTexto de por medio): "DERCO | VOLVO", nunca solo "DERCO". La tarjeta en sí
// NO se toca — sigue con su propio cálculo inline, este helper es una segunda fuente que replica
// la MISMA regla a propósito, para no tocar el render existente de la tarjeta.
const nombreCompletoCliente = (cliente, opCliente) => {
  const empresa = cliente?.empresa || opCliente || "Cliente";
  const marca = cliente?.producto || "";
  const marcaDistinta = marca !== "" && normalizarTexto(marca) !== normalizarTexto(empresa);
  return `${empresa}${marcaDistinta ? ` | ${marca}` : ""}`;
};

// Fecha de cierre de la tarjeta (columna 100%, punto 5, 2ª pasada; SIN año desde el punto 2 de la
// 4ª pasada, 2026-09-26 — "todas las ventas de esa columna son del período ya elegido arriba, el
// año no aporta nada y roba ancho"; el ancho liberado va a la columna del nombre, ver más abajo).
// `fecha_cierre` si existe, si no `mes_cierre` (MM-YYYY) como aproximación — `sales_btl` no tiene
// columna `fecha_cierre` (ver schema.md), cae siempre acá. OJO: `sales.fecha_cierre` arrastra el
// bug de timezone de la regla 10.274 (trigger `set_fecha_cierre_local`, ~3-5h de corrimiento en un
// puñado de casos, fix escrito y sin aplicar) — NO se corrige acá, ver
// docs/dominios/downgrade-pipeline.md. `formatDate` (oportunidadDateStatus.js) da "dd/mm/yyyy"
// SIEMPRE con día/mes de 2 dígitos — el `.slice(0,5)` corta el año de forma segura, no reimplementa
// el parseo/formateo de fecha (esa lógica sigue en UNA sola fuente).
const formatFechaCierreTarjeta = (op) => {
  if (op.fecha_cierre) return formatDate(op.fecha_cierre).slice(0, 5);
  if (op.mes_cierre) {
    const [mm] = op.mes_cierre.split("-");
    const mesLabel = MESES_LETRAS[parseInt(mm, 10) - 1];
    return mesLabel || op.mes_cierre;
  }
  return "—";
};

// Fecha de cierre COMPLETA, CON año — solo para el `title` (tooltip) de la columna 100% (punto 2):
// la tarjeta ya no muestra el año, pero sigue disponible al pasar el mouse. Misma lógica que la
// versión sin año de arriba, antes de recortarle el año — ver ese comentario para el detalle del
// fallback a `mes_cierre` y el bug de timezone heredado.
const formatFechaCierreCompletaTarjeta = (op) => {
  if (op.fecha_cierre) return formatDate(op.fecha_cierre);
  if (op.mes_cierre) {
    const [mm, yyyy] = op.mes_cierre.split("-");
    const mesLabel = MESES_LETRAS[parseInt(mm, 10) - 1];
    return mesLabel ? `${mesLabel} ${yyyy}` : op.mes_cierre;
  }
  return "—";
};

// Tooltip de la pastilla de antigüedad (punto 5, 2ª pasada): mismos días/umbral que el cron
// `downgrade_pipeline_status` (docs/dominios/downgrade-pipeline.md), leídos de getDowngradeInfo —
// nunca recalculados acá. `sales_btl` no está cubierto por el cron: mismo conteo de días, con
// aclaración explícita en vez de la cuenta regresiva.
const tituloAntiguedad = (op, downgradeInfo) => {
  if (!downgradeInfo) return "";
  const { daysSinceUpdate, diasRestantes, estatusDestino } = downgradeInfo;
  if (op.type === "btl") {
    return `${daysSinceUpdate} días sin novedades — sales_btl no está cubierto por el downgrade automático del cron`;
  }
  const destinoLabel = estatusDestino === "0%" ? "0% (fuera del pipeline)" : estatusDestino;
  const plazoLabel =
    diasRestantes > 0 ? `en ${diasRestantes} día${diasRestantes === 1 ? "" : "s"}` : "en la próxima corrida del cron";
  return `${daysSinceUpdate} días sin novedades — baja a ${destinoLabel} ${plazoLabel}`;
};

// Etiqueta de la 1ª celda del cabezal (rediseño v3, docs/decisiones/2026-09-22-pizarra-rediseno-
// v3-tarjetas.md): "Propuestas" en los estatus abiertos, "Ventas" en el 100% (ahí cada fila es una
// venta real, no una alternativa — 10.272).
const getEtiquetaPropuestas = (estatus) => (estatus === "100%" ? "Ventas" : "Propuestas");

// Rótulo de la celda del monto (cabezal-recuadro-una-linea.md): "Proyectado" en los estatus
// abiertos, "Cerrado" en el 100% — existe para que las 3 celdas del cabezal tengan la misma
// estructura número+etiqueta.
const getEtiquetaMonto = (estatus) => (estatus === "100%" ? "Cerrado" : "Proyectado");

// Colores SÓLIDOS de la barra de pipeline — literales del doc (docs/decisiones/2026-09-22-pizarra-
// barra-cabezal-agrupacion.md §1), mismos hexas que --solido de cada columna (.columna[data-estatus]
// en Pizarra.module.scss) — no se inventan acá, se citan.
const PIPELINE_SEGMENTO_ESTILO = {
  "50%": { background: "#ffc107", color: "#222" },
  "75%": { background: "#0d6efd", color: "#fff" },
  "90%": { background: "#dc3545", color: "#fff" },
  "100%": { background: "#198754", color: "#fff" },
};

// Piezas del festejo al cerrar a 100% (2026-09-22) — 24 piezas fijas, generadas UNA vez (no en
// cada render, no `Math.random()` en el JSX). `left` acotado a 0-78% del ancho del tablero a
// propósito: el 100% es la columna de más a la derecha (últimas ~25%) — dejarla casi sin piezas
// evita taparle el monto recién cerrado, que es justo lo que el pedido prohíbe.
const FESTEJO_COLORES = ["#ffc107", "#0d6efd", "#dc3545", "#198754", "#6f42c1", "#fd7e14"];
const FESTEJO_CONFETI = Array.from({ length: 24 }, (_, i) => ({
  left: (i * 47) % 78,
  delay: (i % 6) * 0.08,
  color: FESTEJO_COLORES[i % FESTEJO_COLORES.length],
}));

// Clave estable de una oportunidad (cliente único dentro de una columna puede tener varias) —
// misma composición que el `key` de React usado en las tarjetas.
const claveOportunidad = (op) => `${op.type}-${op.id}`;

// Clave de agrupación por cliente — MISMA composición que ya arma `alternativasPorCliente` en
// `columnas` y el `clientId` de cada tarjeta (op.client_id || op.cliente); extraída para el modo
// Mover (`handleDrop`), que necesita recomputar el grupo fuera del render de la tarjeta.
const getClientKey = (op) => op.client_id || op.cliente;

// Modo Mover (drag-and-drop, 2026-09-22): misma regla que VentasDetalle.isEditableByDate (~línea
// 190, copia local igual que en VentasMiListaClientesV2/VentasMiListaClientes — 3 copias ya
// existentes, no centralizadas; ver 10.243/patrón establecido). Cutoff 2025-04-19: una propuesta
// creada ANTES de esa fecha no se puede editar (y por lo tanto tampoco arrastrar) — si el grupo
// del cliente tiene alguna, el grupo entero queda sin poder moverse (pedido explícito).
const isEditableByDate = (createdAt) => {
  const cutoffDate = new Date("2025-04-19");
  const createdDate = new Date(createdAt);
  return !isNaN(createdDate) && createdDate >= cutoffDate;
};

const DATA_VACIA = {
  oportunidades: [],
  clientes: [],
  users: [],
  intermediarios: [],
  categorias: [],
  subcategorias: [],
};

/**
 * La Pizarra: vista principal del hub de Ventas — la pizarra física de Ignacio, ahora en
 * pantalla. 4 columnas por estatus (50/75/90/100%), oportunidades apiladas por cliente; color y
 * posición ya dicen el estatus. Ver docs/modulos/pizarra-ventas.md.
 */
const Pizarra = ({ renderHeader, tabKey, active } = {}) => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();
  const { getDolarValue, dolarFallback, loaded: ratesLoaded } = useExchangeRates(pais);
  const fallbackRate = dolarFallback?.[pais] || 1;

  const now = new Date();
  // Multi-mes (2026-09-22, docs/decisiones/2026-09-22-pizarra-cabezal-recuadro-una-linea.md —
  // enmienda al final): filtroMes pasa de string a array de "MM". Default (2026-09-23, pedido de
  // Ignacio): array VACÍO — al entrar no hay ningún mes tildado, se ve el año en curso completo
  // (todo lo que está en la calle, no solo lo que cierra este mes). El filtro de mes sigue
  // sirviendo para acotar cuando se quiere mirar un mes solo (ver el predicado de
  // `oportunidadesFiltradas`, que invierte la semántica de "vacío" respecto de la versión
  // anterior — vacío ya no es "ningún mes", es "sin acotar por mes").
  const [filtroMes, setFiltroMes] = useState([]);
  const [filtroAno, setFiltroAno] = useState(String(now.getFullYear()));
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [filtroIntermediario, setFiltroIntermediario] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [viewInUSD, setViewInUSD] = useState(false);

  // Modo Mover (drag-and-drop, 2026-09-22, validado por Ignacio): apagado por defecto — con el
  // modo apagado la Pizarra se comporta EXACTAMENTE como antes (nada arrastrable, el click en el
  // badge ×N sigue desplegando alternativas sin cambios). `dragOrigen` es el grupo cliente+estatus
  // tomado al iniciar el arrastre; `dropTarget` es la columna sobre la que se está pasando (para
  // el resaltado visual); `draggingKey` es la tarjeta tomada (para atenuarla mientras se arrastra);
  // `moviendo` bloquea un segundo arrastre mientras el anterior todavía está escribiendo.
  const [modoMover, setModoMover] = useState(false);
  const [dragOrigen, setDragOrigen] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [draggingKey, setDraggingKey] = useState(null);
  const [moviendo, setMoviendo] = useState(false);

  // Cierre a 100% con confirmación (2026-09-22, habilitado — "el mejor momento del mes"):
  // `confirmacion100` es `null` mientras no hay nada pendiente, o `{ origen, grupo, items,
  // ganadoraDefaultClave }` mientras el modal está abierto (soltar sobre el 100% NUNCA escribe
  // directo — siempre pasa por acá primero). `festejoActivo` dispara la animación de celebración
  // SOLO en un cierre real confirmado — nunca al soltar, nunca al cancelar.
  const [confirmacion100, setConfirmacion100] = useState(null);

  // Zona de descarte — caída a 0% (2026-09-26, punto 1/2 del pedido): `confirmacionCaida` es
  // `null` mientras no hay nada pendiente, o `{ origen, op, nombreCliente, campana, monto }`
  // mientras el modal está abierto — soltar sobre la zona NUNCA escribe directo, igual criterio
  // que el 100%. Sin festejo acá (pedido explícito: "sobrio, sin confetti").
  const [confirmacionCaida, setConfirmacionCaida] = useState(null);
  const [festejoActivo, setFestejoActivo] = useState(false);
  const festejoTimeoutRef = useRef(null);
  useEffect(
    () => () => {
      if (festejoTimeoutRef.current) clearTimeout(festejoTimeoutRef.current);
    },
    []
  );
  const dispararFestejo = useCallback(() => {
    // Respeta prefers-reduced-motion (pedido explícito): directamente NO dispara nada, en vez de
    // mostrar una versión "sin animación" del overlay — más simple y más seguro, cero movimiento.
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (festejoTimeoutRef.current) clearTimeout(festejoTimeoutRef.current);
    setFestejoActivo(true);
    festejoTimeoutRef.current = setTimeout(() => {
      setFestejoActivo(false);
      festejoTimeoutRef.current = null;
    }, 1800);
  }, []);

  // Dropdown de mes con checkboxes — MISMO patrón que VentasDetalle ("Mes cierre"/"Mes factura",
  // handleMesCierreChange/renderMesOptions), no un multiselect propio. Array.from siempre produce
  // una referencia NUEVA aunque el conjunto marcado termine siendo el mismo, así que cualquier
  // interacción real con el dropdown dispara el efecto de cierre de grupos de abajo (comparación
  // por referencia en su dependencia `filtroMes` — no hace falta una clave derivada aparte).
  const handleMesChange = useCallback((e) => {
    const selectedMonths = Array.from(
      e.target.closest(".dropdown-menu").querySelectorAll("input[type=checkbox]:checked"),
      (checkbox) => checkbox.value
    );
    setFiltroMes(selectedMonths);
  }, []);

  const renderMesOptions = () =>
    MESES_LETRAS.map((mes, index) => {
      const value = index < 9 ? `0${index + 1}` : `${index + 1}`;
      return (
        <div key={value} className="form-check left-aligned-checkbox">
          <input
            className="form-check-input"
            type="checkbox"
            id={`pizarraMes${index}`}
            value={value}
            checked={filtroMes.includes(value)}
            onChange={handleMesChange}
          />
          <label className="form-check-label" htmlFor={`pizarraMes${index}`}>
            {mes}
          </label>
        </div>
      );
    });

  // Agrupación de alternativas ×N (punto 3, docs/decisiones/2026-09-22-pizarra-barra-cabezal-
  // agrupacion.md): qué grupos cliente+estatus están desplegados. Clave "estatus:clientId", varios
  // abiertos a la vez. Abrir/cerrar es 100% UI — no dispara ningún recálculo de 10.272 ni de
  // montoTotal/cabecera/barra (esos ya están calculados en `columnas` a partir de las ganadoras,
  // ajenas a este estado).
  const [gruposAbiertos, setGruposAbiertos] = useState(() => new Set());
  const toggleGrupo = useCallback((clave) => {
    setGruposAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });
  }, []);

  // 10.273: "cargando" nunca se muestra como "sin datos" — loader adentro del área de
  // contenido, el header (filtros) sigue publicándose siempre, cargando o no.
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(DATA_VACIA);

  // Invalidación cross-tab del hub (2026-09-22, ver VentasDataVersionContext.js y
  // docs/modulos/ventas-hub.md): `bumpVersionDatos` se llama tras cada escritura propia exitosa
  // (drag-and-drop, cierre a 100%); `datosDesactualizados` se pone en `true` cuando OTRA tab
  // escribió algo mientras la Pizarra estaba activa, o justo al volverse activa si ya había
  // quedado atrás mientras estaba oculta; `marcarSincronizada` avisa "ya estoy al día" después de
  // cada carga real (la inicial y la disparada por staleness). Fuera del hub (`active` es
  // `undefined`) es un no-op completo — standalone sigue igual que siempre.
  const { bump: bumpVersionDatos, stale: datosDesactualizados, marcarSincronizada } = useVentasDataSync(!!active);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    cargarOportunidadesVentas({ pais })
      .then((result) => {
        if (!cancelled) {
          setData(result);
          marcarSincronizada();
        }
      })
      .catch((error) => {
        console.error("Error cargando Pizarra:", error);
        toast.error("Error al cargar datos");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pais, marcarSincronizada]);

  // Recarga silenciosa al quedar desactualizada (2026-09-22) — SIN pasar por `loading` (eso
  // taparía todo el tablero con el loader por un instante, "parpadeo" que la verificación pide
  // evitar): la data vieja se sigue mostrando hasta que la nueva está lista, y recién ahí se
  // reemplaza de una vez. Solo corre cuando `datosDesactualizados` pasa a `true` — nunca mientras
  // la tab está oculta (`useVentasDataSync` ya filtra eso: el chequeo interno solo se hace con
  // `active=true`).
  useEffect(() => {
    if (!datosDesactualizados) return undefined;
    let cancelled = false;
    cargarOportunidadesVentas({ pais })
      .then((result) => {
        if (!cancelled) {
          setData(result);
          marcarSincronizada();
        }
      })
      .catch((error) => {
        console.error("Error recargando Pizarra (datos desactualizados):", error);
        toast.error("Error al recargar datos");
      });
    return () => {
      cancelled = true;
    };
  }, [datosDesactualizados, pais, marcarSincronizada]);

  const { oportunidades, clientes, users, intermediarios, categorias, subcategorias } = data;

  const clientesPais = useMemo(() => clientes.filter((c) => c.pais === pais), [clientes, pais]);
  const {
    filtroEmpresa,
    filtroProducto,
    productosDeEmpresa,
    empresasDeProducto,
    empresaDisabled,
    productoDisabled,
    onChangeEmpresa,
    onChangeProducto,
  } = useEmpresaProductoFilter(clientesPais);

  const clienteById = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [clientes]);

  const clienteCategoriaIdMap = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[c.id] = { categoriaId: c.categoria, subcategoriaId: c.subcategoria_id };
    });
    return map;
  }, [clientes]);

  const clienteIntermediarioMap = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[c.id] = c.agencia;
    });
    return map;
  }, [clientes]);

  const intermediaryMap = useMemo(() => {
    const map = {};
    intermediarios.forEach((i) => {
      map[i.id] = i.nombre;
    });
    return map;
  }, [intermediarios]);

  // Atribución AdLab de una fila (Perú) — réplica exacta de VentasDetalle.esFilaAdLab (misma
  // fuente única con esVentaEnPeriodoBroker) para que el filtro Ejecutivo se comporte igual acá.
  const esFilaAdLab = useCallback(
    (oportunidad, interId, cliente) => {
      const u = users.find((uu) => String(uu.id) === String(oportunidad.ejecutivoId));
      const porEjecutivoBroker = !!u && u.rol === ROLES.BROKER && String(u.agencia) === String(interId);
      const predicadoCuentaPeriodo =
        oportunidad.type === "btl"
          ? String(cliente?.broker_id) === String(interId)
          : esVentaEnPeriodoBroker(oportunidad, cliente);
      return porEjecutivoBroker || predicadoCuentaPeriodo;
    },
    [users]
  );

  const ejecutivoOptionsNodes = useMemo(() => {
    const countryMapping = { Uruguay: "URU", Perú: "PER", Chile: "CHI" };
    const countryCode = countryMapping[pais] || "";
    const ejecutivosUnicos = users
      .filter((user) => {
        const userPais = user.pais || "";
        const userRol = user.rol ? user.rol.toLowerCase().trim() : "";
        return (
          (userPais === countryCode || !countryCode) &&
          ["comercial", "gerencia"].includes(userRol) &&
          user.habilitado === true
        );
      })
      .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`));

    const brokerInterIds = [
      ...new Set(
        users
          .filter((u) => (u.pais || "") === countryCode || !countryCode)
          .filter((u) => u.rol === ROLES.BROKER && u.agencia)
          .map((u) => String(u.agencia))
      ),
    ];

    return [
      ...ejecutivosUnicos.map((user) => (
        <option key={`u-${user.id}`} value={user.id}>{`${user.nombre} ${user.apellido}`}</option>
      )),
      ...brokerInterIds.map((interId) => (
        <option key={`b-${interId}`} value={`broker:${interId}`}>
          {intermediaryMap[interId] || `Broker ${interId}`}
        </option>
      )),
      ...(brokerInterIds.length > 0
        ? [
            <option key="grupo-visiona" value="grupo:visiona">
              Visiona
            </option>,
          ]
        : []),
    ];
  }, [users, pais, intermediaryMap]);

  const oportunidadesFiltradas = useMemo(
    () =>
      oportunidades.filter((op) => {
        if (op.eliminada || op.estatus === "0%") return false;
        // Multi-mes: el año siempre filtra (filtroAno sigue single, obligatorio). Semántica de
        // filtroMes=[] (vacío, 2026-09-23 — invertida respecto de la versión anterior): SIN
        // ACOTAR por mes, muestra los 12 meses del año elegido — es el estado por DEFECTO al
        // entrar (todo lo que está en la calle ese año, no solo lo que cierra este mes). Elegir
        // uno o más meses sigue acotando de la forma de siempre.
        const [mesOp, anoOp] = (op.mes_cierre || "").split("-");
        if (anoOp !== filtroAno) return false;
        if (filtroMes.length > 0 && !filtroMes.includes(mesOp)) return false;

        const cliente = clienteById[op.client_id];
        const empresaMatch = !filtroEmpresa || (cliente && cliente.empresa === filtroEmpresa);
        const productoMatch = !filtroProducto || (cliente && cliente.producto === filtroProducto);
        const categoriaMatch =
          !filtroCategoria || String(clienteCategoriaIdMap[op.client_id]?.categoriaId) === String(filtroCategoria);
        const subcategoriaMatch =
          !filtroSubcategoria ||
          String(clienteCategoriaIdMap[op.client_id]?.subcategoriaId) === String(filtroSubcategoria);

        let ejecutivoMatch;
        if (!filtroEjecutivo) {
          ejecutivoMatch = true;
        } else if (filtroEjecutivo.startsWith("broker:")) {
          ejecutivoMatch = esFilaAdLab(op, filtroEjecutivo.slice("broker:".length), cliente);
        } else if (filtroEjecutivo === "grupo:visiona") {
          ejecutivoMatch = !esFilaAdLab(op, AGENCIA_ADLAB_PERU, cliente);
        } else {
          ejecutivoMatch = String(op.ejecutivoId) === String(filtroEjecutivo);
        }

        const intermediaryId = clienteIntermediarioMap[op.client_id] || null;
        const intermediarioMatch = matchAgenciaFilter(filtroIntermediario, intermediaryId, pais);

        return (
          empresaMatch && productoMatch && categoriaMatch && subcategoriaMatch && ejecutivoMatch && intermediarioMatch
        );
      }),
    [
      oportunidades,
      clienteById,
      filtroMes,
      filtroAno,
      filtroEmpresa,
      filtroProducto,
      filtroCategoria,
      filtroSubcategoria,
      filtroEjecutivo,
      filtroIntermediario,
      pais,
      clienteCategoriaIdMap,
      clienteIntermediarioMap,
      esFilaAdLab,
    ]
  );

  // Al cambiar CUALQUIER filtro (o el toggle de dólares) se cierran todos los grupos abiertos —
  // punto 3 del doc. `viewInUSD` no dispara `oportunidadesFiltradas` (no filtra datos), así que va
  // en su propio efecto con exactamente los 9 disparadores que lista el doc. `filtroMes` (2026-09-
  // 22, multi-mes) sigue disparando bien: handleMesChange arma el array con Array.from en cada
  // interacción, así que SIEMPRE es una referencia nueva — Object.is en la dependencia de abajo
  // nunca la confunde con la anterior, sin necesitar una clave derivada aparte.
  useEffect(() => {
    setGruposAbiertos(new Set());
  }, [
    filtroMes,
    filtroAno,
    filtroEmpresa,
    filtroProducto,
    filtroCategoria,
    filtroSubcategoria,
    filtroIntermediario,
    filtroEjecutivo,
    viewInUSD,
  ]);

  // Importe en moneda local con rate histórico por fecha (10.185) — misma fórmula que
  // VentasDetalle.getImporteLocal. Si moneda_dolar=false, retorna el importe sin tocar.
  const getImporteLocal = useCallback(
    (op) => {
      const importe = op.type === "btl" ? op.total_investment || 0 : parseFloat(op.importe) || 0;
      if (!op.moneda_dolar) return importe;
      const rate = getRateForOpp(op, getDolarValue, fallbackRate);
      return importe * rate;
    },
    [getDolarValue, fallbackRate]
  );

  // Base de comparación para elegir la GANADORA por cliente (regla 10.272): siempre en
  // equivalente-USD — el criterio de "menor monto" tiene que comparar magnitudes de la misma
  // unidad, sin importar el toggle "Ver en dólares" (que solo decide cómo se MUESTRA, no cómo
  // se elige la ganadora). Evita que una alternativa nativa USD parezca "menor" solo por no
  // convertir. Mismo winner que elegiría VentasDetalle en modo USD.
  const getValorSeleccion = useCallback(
    (op) => getImporteEnUSD(op, getDolarValue, fallbackRate),
    [getDolarValue, fallbackRate]
  );

  const formatMonedaLocal = useCallback(
    (valor) => {
      if (valor === null || valor === undefined || isNaN(valor)) return "-";
      if (pais === "Perú") return formatAmount(valor, "PEN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return formatAmount(valor, "CLP");
    },
    [pais, formatAmount]
  );

  // Ventana de la columna 100% (2026-09-23, pedido de Ignacio) — DISTINTA de la de 50/75/90. La
  // pizarra es dos miradas a la vez: una mirada ABIERTA de lo que está en la calle (50/75/90, año
  // completo sin acotar por mes — no se toca) y una mirada MENSUAL del cierre (100%: cuánto llevo
  // cerrado ESTE mes, no todo el año). Sin meses tildados, el 100% se acota SOLO al mes en curso
  // — el 1 de octubre la columna arranca en cero y se llena de nuevo. Si el usuario tilda uno o
  // más meses, ESE filtro pisa las 4 columnas por igual (acá no aplica ninguna ventana especial,
  // `ventana100Mes` da `null`). Caso de borde decidido por Ignacio (no a criterio propio): con el
  // selector de Año en un año que NO es el corriente, el 100% muestra los 12 meses de ese año —
  // acotar al mes corriente de un año pasado dejaría la columna vacía y parecería un bug.
  const anoActualStr = String(now.getFullYear());
  const mesActualStr = String(now.getMonth() + 1).padStart(2, "0");
  const ventana100Mes = filtroMes.length === 0 && filtroAno === anoActualStr ? mesActualStr : null;
  const ventana100Label = ventana100Mes ? MESES_LETRAS[parseInt(ventana100Mes, 10) - 1] : null;

  // Formato de UNA oportunidad en la moneda unificada activa (tarjeta e hijas del despliegue
  // comparten esta misma fuente — nunca se reimplementa el ternario por separado).
  const formatMontoOp = useCallback(
    (op) =>
      viewInUSD
        ? formatAmount(getImporteEnUSD(op, getDolarValue, fallbackRate), "USD")
        : formatMonedaLocal(getImporteLocal(op)),
    [viewInUSD, getDolarValue, fallbackRate, formatMonedaLocal, getImporteLocal, formatAmount]
  );

  const columnas = useMemo(
    () =>
      ESTATUS_COLUMNAS.map((estatus) => {
        // 100% (2026-09-23): capa de ventana ADICIONAL, solo acá — `oportunidadesFiltradas` sigue
        // siendo la base compartida de las 4 columnas (año + meses tildados), sin tocar. Cuando
        // `ventana100Mes` es verdadero (sin meses tildados y año = año en curso), el 100% se acota
        // además al mes en curso; 50/75/90 ignoran esta capa por completo.
        const opsDelEstatus = oportunidadesFiltradas.filter((op) => {
          if (op.estatus !== estatus) return false;
          if (estatus === "100%" && ventana100Mes) {
            const [mesOp] = (op.mes_cierre || "").split("-");
            if (mesOp !== ventana100Mes) return false;
          }
          return true;
        });
        // OJO 10.272 + multi-mes: la ganadora se elige sobre TODO el conjunto ya filtrado por
        // meses, no mes a mes. Un cliente con una propuesta en septiembre y otra en octubre (ambos
        // meses elegidos) aporta UNA sola tarjeta acá, la de menor monto — igual que hoy elige una
        // sola entre dos propuestas del MISMO mes. Es el comportamiento correcto (10.272 no dice
        // "por mes", dice "por cliente y estatus, dentro del filtro vigente") — no "arreglar" esto
        // sumando las dos.
        const ganadoras = seleccionarOportunidadesPorEstatus(opsDelEstatus, getValorSeleccion, estatus);

        // Cabecera: moneda UNIFICADA (2026-09-22) — un solo número, en la misma unidad que
        // muestran las tarjetas de esta columna. Nunca vuelve a mezclar "$ X + US$ Y": en modo
        // local, TODA ganadora (incluida moneda_dolar=true) se convierte a local vía
        // getImporteLocal; en modo USD, TODA ganadora vía getImporteEnUSD. Mismo TC histórico
        // por fecha que ya existe (getRateForOpp/getImporteEnUSD) — no se introduce ninguno
        // nuevo, y getValorSeleccion (10.272, siempre equivalente-USD) no se toca.
        const getValorMostrado = viewInUSD ? (op) => getImporteEnUSD(op, getDolarValue, fallbackRate) : getImporteLocal;
        const montoTotal = ganadoras.reduce((s, op) => s + (Number(getValorMostrado(op)) || 0), 0);

        const etiquetas = ganadoras
          .map((op) => ({ op, valorOrden: getValorSeleccion(op) }))
          .sort((a, b) => b.valorOrden - a.valorOrden)
          .map(({ op }) => op);

        // Agrupación de alternativas ×N (punto 3): TODAS las propuestas de cada cliente en este
        // estatus, para el badge y el despliegue — de opsDelEstatus, ya en memoria, sin query
        // nueva. NO aplica al 100% (ahí cada fila es una venta cerrada distinta, no una
        // alternativa — 10.272, §4 del doc); Map vacío ahí, así el badge nunca aparece.
        const alternativasPorCliente = new Map();
        if (estatus !== "100%") {
          opsDelEstatus.forEach((op) => {
            const clientId = op.client_id || op.cliente;
            if (!alternativasPorCliente.has(clientId)) alternativasPorCliente.set(clientId, []);
            alternativasPorCliente.get(clientId).push(op);
          });
        }

        return {
          estatus,
          montoTotal,
          propuestas: opsDelEstatus.length,
          clientesDistintos: new Set(opsDelEstatus.map((op) => op.client_id || op.cliente)).size,
          etiquetas,
          alternativasPorCliente,
        };
      }),
    [oportunidadesFiltradas, getValorSeleccion, viewInUSD, getDolarValue, fallbackRate, getImporteLocal, ventana100Mes]
  );

  // Total del pipeline (punto 1): suma de los montoTotal ya calculados por columna — misma unidad
  // que el cabezal/tarjetas, ningún recálculo aparte. Si es 0, la barra no se renderiza.
  const totalPipeline = useMemo(() => columnas.reduce((s, c) => s + (Number(c.montoTotal) || 0), 0), [columnas]);
  const hayPipeline = totalPipeline > 0;

  // Resuelve el grupo que corresponde a un `dragOrigen` — SIEMPRE contra `oportunidadesFiltradas`
  // (el MISMO universo que arma `alternativasPorCliente`/el badge ×N que el usuario vio al tomar
  // la tarjeta), nunca contra una referencia capturada al iniciar el arrastre, para no mover datos
  // potencialmente stale. Origen "100%": la propuesta es SIEMPRE una sola, identificada por
  // `opClave` — un cliente puede tener varias ventas cerradas distintas en esa columna (no hay
  // agrupación ahí, 10.272 §4), filtrar solo por clientId movería TODAS por error. Cualquier otro
  // origen: el GRUPO completo del cliente en esa columna (clientId + estatus), como siempre.
  const resolverGrupoOrigen = useCallback(
    (origen) => {
      if (origen.estatus === "100%") {
        const op = oportunidadesFiltradas.find((o) => claveOportunidad(o) === origen.opClave);
        return op ? [op] : [];
      }
      return oportunidadesFiltradas.filter(
        (op) => op.estatus === origen.estatus && String(getClientKey(op)) === String(origen.clientId)
      );
    },
    [oportunidadesFiltradas]
  );

  // Modo Mover — soltar el grupo del cliente en `destino` (2026-09-22, validado por Ignacio).
  // Se mueve el GRUPO COMPLETO del cliente en la columna de origen (todas sus propuestas ahí, no
  // solo la ganadora visible) — decisión explícita: si General Motors tiene 3 propuestas en 50%,
  // las 3 pasan al destino.
  const handleDrop = useCallback(
    async (destino) => {
      if (!modoMover || moviendo) return;
      // El 100% como destino NUNCA pasa por acá — tiene su propio flujo con confirmación
      // (handleDropSobre100/handleConfirmarCierre100, ver más abajo): mover el grupo entero
      // declararía varias ventas de una (10.272: al 100% se suma TODO, no se elige una sola).
      if (destino === "100%") return;
      const origen = dragOrigen;
      setDragOrigen(null);
      setDropTarget(null);
      setDraggingKey(null);
      if (!origen || origen.estatus === destino) return;

      const grupo = resolverGrupoOrigen(origen);
      if (grupo.length === 0) return;

      const nombreCliente = nombreCompletoCliente(clienteById[origen.clientId], grupo[0]?.cliente);

      // Si CUALQUIER propuesta del grupo no es editable (isEditableByDate), el grupo entero NO se
      // mueve — se avisa por qué, nada se escribe.
      if (!grupo.every((op) => isEditableByDate(op.created_at))) {
        toast.error(`${nombreCliente}: no se puede mover, el grupo tiene una propuesta no editable.`);
        return;
      }

      setMoviendo(true);
      const nowIso = new Date().toISOString();
      const clavesGrupo = new Set(grupo.map(claveOportunidad));

      // Optimista: la tarjeta se mueve YA en pantalla — totales, barra y conteos se recalculan
      // solos (derivan de `data.oportunidades` vía los `useMemo` de arriba, sin tocarlos).
      setData((prev) => ({
        ...prev,
        oportunidades: prev.oportunidades.map((op) =>
          clavesGrupo.has(claveOportunidad(op)) ? { ...op, estatus: destino, updated_at: nowIso } : op
        ),
      }));

      // Cada oportunidad se escribe por el camino de SU tabla — nunca supabase directo acá.
      // BTL: la columna real es `status` (NUMÉRICA sin "%", ver VentasBTL statusMap) y el PK real
      // es `full_id` (sales_btl.id) — `op.id` en la Pizarra es `numeric_id`, solo de display (ver
      // cargarOportunidadesVentas.js).
      const resultados = await Promise.allSettled(
        grupo.map((op) => {
          if (op.type === "sale") {
            return updateOportunidad({ id: op.id, oportunidad: { estatus: destino, updated_at: nowIso } });
          }
          if (op.type === "contrato") {
            return updateContrato({ id: op.id, contrato: { estatus: destino, updated_at: nowIso } });
          }
          return updateOportunidadBTL({
            id: op.full_id,
            oportunidad: { status: Number(destino.replace("%", "")), updated_at: nowIso },
          });
        })
      );

      const fallidas = [];
      const conSnapshotFallo = [];
      grupo.forEach((op, idx) => {
        const r = resultados[idx];
        if (r.status === "rejected") {
          fallidas.push(op);
          console.error(`Error moviendo oportunidad ${claveOportunidad(op)} a ${destino}:`, r.reason);
        } else if (op.type !== "btl" && r.value?.snapshotFallo) {
          conSnapshotFallo.push(op);
        }
      });

      if (fallidas.length === 0) {
        // Invalidación cross-tab (2026-09-22): escritura real y exitosa — avisa al resto del hub
        // (Detalle, CRM) que sus datos de oportunidades quedaron atrás. La propia Pizarra ya
        // refleja el cambio (optimista, arriba) — `bumpVersionDatos` la marca "al día" con la
        // nueva versión al mismo tiempo, así que este mismo bump nunca la recarga a sí misma.
        bumpVersionDatos();
        toast.success(
          grupo.length > 1
            ? `${nombreCliente}: ${grupo.length} propuestas movidas a ${destino}`
            : `${nombreCliente} movido a ${destino}`
        );
        // Efecto secundario ESPERADO (no a evitar, a documentar — ver ficha): updated_at recién
        // escrito reinicia el reloj del cron downgrade_pipeline_status para cada propuesta movida.
        if (conSnapshotFallo.length > 0) {
          toast.error(`${nombreCliente}: se movió, pero la foto de comisión no se pudo actualizar (queda registrado).`);
        }
        setMoviendo(false);
        return;
      }

      // Falla parcial o total (10.177/10.191): NO dejar la pantalla mintiendo — informar
      // exactamente cuáles quedaron movidas y cuáles no, y refrescar desde la base.
      const detalleFallidas = fallidas.map((op) => op.campana || `#${op.id}`).join(", ");
      toast.error(
        `${nombreCliente}: falló mover ${fallidas.length} de ${grupo.length} (${detalleFallidas}) a ${destino}. Recargando desde la base...`,
        { duration: 6000 }
      );

      // Al menos una escritura real tuvo éxito (falla PARCIAL, no total) — el resto del hub
      // también tiene que enterarse, igual que en el camino sin fallas.
      if (fallidas.length < grupo.length) bumpVersionDatos();

      try {
        const fresh = await cargarOportunidadesVentas({ pais });
        setData(fresh);
        marcarSincronizada();
      } catch (reloadError) {
        console.error("Error recargando Pizarra tras falla de movimiento:", reloadError);
        // El refetch también falló — revertir localmente en vez de dejar la pantalla mintiendo.
        setData((prev) => ({
          ...prev,
          oportunidades: prev.oportunidades.map((op) =>
            clavesGrupo.has(claveOportunidad(op)) ? { ...op, estatus: origen.estatus } : op
          ),
        }));
      } finally {
        setMoviendo(false);
      }
    },
    [modoMover, moviendo, dragOrigen, resolverGrupoOrigen, clienteById, pais, bumpVersionDatos, marcarSincronizada]
  );

  // Soltar sobre el 100% (2026-09-22, habilitado): NUNCA escribe directo — abre la confirmación.
  // Al 100% se suma TODO (10.272), así que mover un grupo entero declararía varias ventas de una;
  // acá solo se RESUELVE el grupo y se arma lo que el modal necesita, la escritura real vive en
  // `handleConfirmarCierre100`, recién tras la confirmación explícita.
  const handleDropSobre100 = useCallback(() => {
    if (!modoMover || moviendo) return;
    const origen = dragOrigen;
    setDragOrigen(null);
    setDropTarget(null);
    setDraggingKey(null);
    if (!origen || origen.estatus === "100%") return;

    const grupo = resolverGrupoOrigen(origen);
    if (grupo.length === 0) return;

    if (!grupo.every((op) => isEditableByDate(op.created_at))) {
      const nombreCliente = nombreCompletoCliente(clienteById[origen.clientId], grupo[0]?.cliente);
      toast.error(`${nombreCliente}: no se puede mover, el grupo tiene una propuesta no editable.`);
      return;
    }

    const items = grupo.map((op) => ({
      clave: claveOportunidad(op),
      campana: op.campana || "Sin campaña",
      monto: formatMontoOp(op),
    }));
    // Default: la propuesta que el usuario efectivamente tomó con el mouse — si por algún motivo
    // ya no está en el grupo recién resuelto (raro, cambió el filtro en el medio), cae a la 1ª.
    const ganadoraDefaultClave = items.some((it) => it.clave === origen.opClave) ? origen.opClave : items[0].clave;

    setConfirmacion100({ origen, grupo, items, ganadoraDefaultClave });
  }, [modoMover, moviendo, dragOrigen, resolverGrupoOrigen, clienteById, formatMontoOp]);

  // Confirmar el cierre a 100% (2026-09-22) — se llama SOLO desde el modal, tras elegir la
  // ganadora (si hay varias) y decidir qué otras eliminar. Al 100% se mueve UNA sola propuesta;
  // las demás del grupo se ELIMINAN (baja lógica, mismo camino que ya usa Detalle — nunca pasan a
  // 0%: el 0% es memoria de intentos que no salieron, y una alternativa descartada porque se ganó
  // otra del mismo grupo nunca fue un intento fallido).
  const handleConfirmarCierre100 = useCallback(
    async (ganadoraClave, eliminarClaves) => {
      if (!confirmacion100) return;
      const { grupo, origen } = confirmacion100;
      const ganadoraOp = grupo.find((op) => claveOportunidad(op) === ganadoraClave);
      setConfirmacion100(null);
      if (!ganadoraOp) return;

      const opsAEliminar = grupo.filter(
        (op) => claveOportunidad(op) !== ganadoraClave && eliminarClaves.has(claveOportunidad(op))
      );

      setMoviendo(true);
      const nowIso = new Date().toISOString();
      const clavesEliminadas = new Set(opsAEliminar.map(claveOportunidad));
      const nombreCliente = nombreCompletoCliente(clienteById[origen.clientId], grupo[0]?.cliente);

      // Optimista: la ganadora pasa a 100%, las eliminadas desaparecen YA (mismo resultado que
      // produce `cargarOportunidadesVentas`, que ya filtra `eliminada` en el origen — sacarlas
      // del array local reproduce eso sin reimplementar el filtro). Las que quedan sin marcar
      // (no elegidas, no eliminadas) no se tocan: siguen en su columna de origen tal cual.
      setData((prev) => ({
        ...prev,
        oportunidades: prev.oportunidades
          .filter((op) => !clavesEliminadas.has(claveOportunidad(op)))
          .map((op) => (claveOportunidad(op) === ganadoraClave ? { ...op, estatus: "100%", updated_at: nowIso } : op)),
      }));

      // Cada oportunidad por el camino de SU tabla, nunca supabase directo. BTL: no tiene
      // `fecha_cierre` ni el trigger de sales/sales_contratos (verificado contra la definición
      // real de `set_fecha_cierre_local` — solo está en `sales`/`sales_contratos`, `sales_btl` no
      // tiene esa columna); su estatus real es `status`, NUMÉRICO sin "%" (VentasBTL statusMap).
      const escribirEstatus100 = (op) => {
        if (op.type === "sale") {
          return updateOportunidad({ id: op.id, oportunidad: { estatus: "100%", updated_at: nowIso } });
        }
        if (op.type === "contrato") {
          return updateContrato({ id: op.id, contrato: { estatus: "100%", updated_at: nowIso } });
        }
        return updateOportunidadBTL({ id: op.full_id, oportunidad: { status: 100, updated_at: nowIso } });
      };
      const escribirEliminar = (op) => {
        if (op.type === "sale") return updateOportunidad({ id: op.id, oportunidad: { eliminada: true } });
        if (op.type === "contrato") return updateContrato({ id: op.id, contrato: { eliminada: true } });
        return updateOportunidadBTL({ id: op.full_id, oportunidad: { eliminada: true } });
      };

      try {
        const resGanadora = await escribirEstatus100(ganadoraOp);
        // Escritura real y exitosa — avisa al resto del hub (mismo mecanismo que handleDrop).
        bumpVersionDatos();
        toast.success(`${nombreCliente} cerrado al 100% — ${formatMontoOp(ganadoraOp)}`);
        if (ganadoraOp.type !== "btl" && resGanadora?.snapshotFallo) {
          toast.error(`${nombreCliente}: se cerró, pero la foto de comisión no se pudo actualizar (queda registrado).`);
        }
        // Festejo SOLO acá — el cierre real ya tuvo éxito. Si las eliminaciones de abajo fallan
        // parcialmente, el cierre en sí sigue siendo válido: no se cancela retroactivamente.
        dispararFestejo();

        if (opsAEliminar.length > 0) {
          const resultados = await Promise.allSettled(opsAEliminar.map(escribirEliminar));
          const fallidas = [];
          opsAEliminar.forEach((op, idx) => {
            if (resultados[idx].status === "rejected") {
              fallidas.push(op);
              console.error(`Error eliminando alternativa ${claveOportunidad(op)}:`, resultados[idx].reason);
            }
          });
          if (fallidas.length > 0) {
            const detalle = fallidas.map((op) => op.campana || `#${op.id}`).join(", ");
            toast.error(
              `${nombreCliente}: el cierre quedó bien, pero no se pudo eliminar ${fallidas.length} alternativa(s) (${detalle}). Recargando...`,
              { duration: 6000 }
            );
            const fresh = await cargarOportunidadesVentas({ pais });
            setData(fresh);
            marcarSincronizada();
          }
        }
      } catch (error) {
        console.error("Error cerrando al 100%:", error);
        toast.error(`${nombreCliente}: no se pudo cerrar al 100%. Recargando desde la base...`, { duration: 6000 });
        try {
          const fresh = await cargarOportunidadesVentas({ pais });
          setData(fresh);
          marcarSincronizada();
        } catch (reloadError) {
          console.error("Error recargando Pizarra tras falla de cierre a 100%:", reloadError);
          // Ni el cierre ni las eliminaciones llegaron a escribirse — revertir el optimismo
          // completo: la ganadora vuelve a su estatus de origen, las "eliminadas" reaparecen
          // (nunca se tocó su `eliminada` real).
          setData((prev) => {
            const clavesPresentes = new Set(prev.oportunidades.map(claveOportunidad));
            const faltantes = grupo.filter(
              (op) => !clavesPresentes.has(claveOportunidad(op)) && claveOportunidad(op) !== ganadoraClave
            );
            return {
              ...prev,
              oportunidades: [
                ...prev.oportunidades.map((op) =>
                  claveOportunidad(op) === ganadoraClave ? { ...op, estatus: origen.estatus } : op
                ),
                ...faltantes,
              ],
            };
          });
        }
      } finally {
        setMoviendo(false);
      }
    },
    [confirmacion100, clienteById, pais, formatMontoOp, bumpVersionDatos, marcarSincronizada, dispararFestejo]
  );

  // Soltar sobre la zona de descarte (2026-09-26, punto 1/2 del pedido de Ignacio): NUNCA escribe
  // directo, abre la confirmación — mismo criterio que el 100%. A diferencia de 50/75/90↔50/75/90
  // (mueve el GRUPO completo del cliente) y calcado del origen "100%" de siempre, la caída a 0%
  // mueve SOLO la propuesta arrastrada — identificada por `opClave` exacta, nunca por
  // `clientId`+`estatus`. Alternativas hermanas del mismo cliente en la misma columna siguen
  // siendo opciones vivas; arrastrar UNA a "Se cayó" no tumba a las demás con ella — coincide
  // además con el copy pedido del modal, siempre en singular ("la propuesta", nunca "las
  // propuestas" ni "el grupo").
  const handleDropSobre0 = useCallback(() => {
    if (!modoMover || moviendo) return;
    const origen = dragOrigen;
    setDragOrigen(null);
    setDropTarget(null);
    setDraggingKey(null);
    if (!origen) return;

    const op = oportunidadesFiltradas.find((o) => claveOportunidad(o) === origen.opClave);
    if (!op) return;

    const nombreCliente = nombreCompletoCliente(clienteById[op.client_id], op.cliente);
    if (!isEditableByDate(op.created_at)) {
      toast.error(`${nombreCliente}: no se puede mover, la propuesta no es editable.`);
      return;
    }

    setConfirmacionCaida({ origen, op, nombreCliente, campana: op.campana || "Sin campaña", monto: formatMontoOp(op) });
  }, [modoMover, moviendo, dragOrigen, oportunidadesFiltradas, clienteById, formatMontoOp]);

  // Confirmar la caída a 0% (2026-09-26) — se llama SOLO desde el modal. Mismo camino de
  // escritura que `handleDrop` (updateOportunidad/updateContrato/updateOportunidadBTL — nunca
  // supabase directo, nunca un camino nuevo, respetando las diferencias por tabla: `sales_btl` usa
  // `status` NUMÉRICO sin "%" y PK `full_id`, ver más arriba), mismo patrón optimista+rollback. NO
  // manda ningún campo de motivo de pérdida — no existe todavía en el schema (lo está definiendo
  // la conversación del modelo de oportunidad), así que no hay nada a medio hacer esperándolo acá.
  // Sin festejo: caída ≠ cierre, es sobria a propósito (pedido explícito).
  const handleConfirmarCaida0 = useCallback(async () => {
    if (!confirmacionCaida) return;
    const { op, origen, nombreCliente } = confirmacionCaida;
    setConfirmacionCaida(null);

    setMoviendo(true);
    const nowIso = new Date().toISOString();
    const clave = claveOportunidad(op);

    // Optimista — la tarjeta se va YA: `oportunidadesFiltradas` ya excluye `estatus === "0%"`
    // (mismo filtro de siempre), así que este solo cambio ya la saca de las 4 columnas sin tocar
    // ningún useMemo derivado.
    setData((prev) => ({
      ...prev,
      oportunidades: prev.oportunidades.map((o) =>
        claveOportunidad(o) === clave ? { ...o, estatus: "0%", updated_at: nowIso } : o
      ),
    }));

    let resultado;
    try {
      if (op.type === "sale") {
        resultado = await updateOportunidad({ id: op.id, oportunidad: { estatus: "0%", updated_at: nowIso } });
      } else if (op.type === "contrato") {
        resultado = await updateContrato({ id: op.id, contrato: { estatus: "0%", updated_at: nowIso } });
      } else {
        resultado = await updateOportunidadBTL({ id: op.full_id, oportunidad: { status: 0, updated_at: nowIso } });
      }
    } catch (error) {
      console.error(`Error moviendo oportunidad ${clave} a 0%:`, error);
      toast.error(`${nombreCliente}: no se pudo marcar como caída. Recargando desde la base...`, { duration: 6000 });
      try {
        const fresh = await cargarOportunidadesVentas({ pais });
        setData(fresh);
        marcarSincronizada();
      } catch (reloadError) {
        console.error("Error recargando Pizarra tras falla de caída a 0%:", reloadError);
        // Ni el refetch de rescate llegó — revertir el optimismo localmente, último recurso.
        setData((prev) => ({
          ...prev,
          oportunidades: prev.oportunidades.map((o) =>
            claveOportunidad(o) === clave ? { ...o, estatus: origen.estatus } : o
          ),
        }));
      }
      setMoviendo(false);
      return;
    }

    // Invalidación cross-tab (mismo mecanismo que handleDrop/handleConfirmarCierre100) — Detalle
    // y el CRM se enteran de que esta propuesta cayó sin necesidad de recargar la página.
    bumpVersionDatos();
    toast.success(`${nombreCliente}: se cayó — pasó a 0%`);
    if (op.type !== "btl" && resultado?.snapshotFallo) {
      toast.error(`${nombreCliente}: se movió, pero la foto de comisión no se pudo actualizar (queda registrado).`);
    }
    setMoviendo(false);
  }, [confirmacionCaida, pais, bumpVersionDatos, marcarSincronizada]);

  // Handlers HTML5 nativo (sin librería — 5 zonas alcanzan: las 4 columnas + la zona de descarte,
  // 2026-09-26, reusa el mismo helper con el sentinela `estatus === "0%"` — nunca fue una columna
  // real, `ESTATUS_COLUMNAS` sigue con solo 4). Las 4 columnas aceptan drop desde 2026-09-22 (100%
  // habilitado ese día) — la diferencia entre destinos vive en `onDrop`, no en si se acepta o no
  // el gesto. `dragLeave` chequea `relatedTarget` para no parpadear el resaltado al pasar entre
  // hijos DENTRO de la misma zona (cabezal/cuerpo/tarjetas en una columna; flecha/texto en la zona
  // de descarte).
  const dragZoneProps = useCallback(
    (estatus) => {
      if (!modoMover) return {};
      return {
        onDragEnter: (e) => {
          e.preventDefault();
        },
        onDragOver: (e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDropTarget((prev) => (prev === estatus ? prev : estatus));
        },
        onDragLeave: (e) => {
          if (e.currentTarget.contains(e.relatedTarget)) return;
          setDropTarget((prev) => (prev === estatus ? null : prev));
        },
        onDrop: (e) => {
          e.preventDefault();
          if (estatus === "100%") {
            handleDropSobre100();
          } else if (estatus === "0%") {
            handleDropSobre0();
          } else {
            handleDrop(estatus);
          }
        },
      };
    },
    [modoMover, handleDrop, handleDropSobre100, handleDropSobre0]
  );

  // Tablero de alto fijo, tipo kanban (2026-09-22, FIX de un bug real reportado por Ignacio: las
  // columnas se salían de la pantalla y no scrolleaba ni la columna ni la página). La versión
  // anterior media el alto con una resta encadenada de 3 términos (header + barra de pipeline +
  // 30px) — si CUALQUIERA de esos términos quedaba corto (por el motivo que fuera), el tablero
  // medía de más, .cuerpo nunca llegaba a desbordar y por lo tanto nunca scrolleaba. Reemplazado
  // por UNA sola medición directa: la distancia REAL de .tableroColumnas al borde superior del
  // viewport (`getBoundingClientRect().top`) ya incluye TODO lo que hay arriba —header, barra de
  // pipeline, paddings— sin necesidad de sumarlo/restarlo a mano. El tablero SIEMPRE termina
  // exactamente donde termina la pantalla, sin importar qué haya arriba ni cuánto mida.
  //   alto = window.innerHeight − top − MARGEN_INFERIOR − MARGEN_SEGURIDAD
  // MARGEN_INFERIOR (15px) es el padding-bottom del wrapper del shell (Ventas.module.scss
  // .wrapper) — el único "margen real" que la medición del top no captura, porque top es la
  // distancia de ARRIBA, no de abajo. MARGEN_SEGURIDAD (2px, enmienda "fix loop ResizeObserver"
  // 2026-09-22) es puro colchón anti-oscilación: sin él, un redondeo de sub-pixel que empuje el
  // alto UN PIXEL de más hace aparecer la barra de scroll de la página, que angosta el ancho
  // disponible y corre TODO horizontalmente (incluido el header) — con el margen, el tablero
  // siempre mide un poco MENOS que el espacio real disponible, nunca justo al límite.
  //
  // FIX 2026-09-22 (bug real: "ResizeObserver loop completed with undelivered notifications" +
  // el header corriéndose unos px al ir a Mapa de Calor y volver) — dos causas, ambas de acá:
  //
  // 1) EL OBSERVER OBSERVABA EL MISMO ELEMENTO CUYO ALTO FIJA EL EFECTO (lazo). Antes:
  //    `observer.observe(tableroColumnasRef.current)` — el propio `.tableroColumnas`. Cada vez que
  //    `recalcularAlto` escribía `el.style.height`, ESO ES una mutación de tamaño del elemento
  //    OBSERVADO, así que el navegador vuelve a encolar una notificación para el mismo tick — el
  //    guard de "no reescribir si el alto no cambió" no alcanza a evitarlo, porque la primera
  //    escritura YA disparó el re-chequeo antes de que el guard pudiera actuar; si esto no se
  //    estabiliza dentro de un frame, Chrome corta el lazo con el error del síntoma 1. Fix: el
  //    `ResizeObserver` ya NO observa `.tableroColumnas` — observa `arribaDelTableroRef` (el
  //    centinela de arriba, ver JSX): un elemento que el efecto NUNCA mide ni mutila. Cuando ESE
  //    centinela cambia de tamaño (aparece/desaparece la barra de pipeline, o pasa de oculto a
  //    visible al volver de otra tab — ver punto 2), se dispara `recalcularAlto`, que sigue
  //    midiendo y escribiendo SOLO en `.tableroColumnas` — un elemento distinto del observado, así
  //    que esa escritura no re-dispara el mismo observer. Sin este centinela ya no hace falta el
  //    `hayPipeline` en las dependencias del efecto (antes forzaba re-armar todo el listener para
  //    capturar el cambio de tamaño de la barra de pipeline a mano) — el observer lo capta solo.
  //
  // 2) NO MEDIR CON LA TAB OCULTA. El hub mantiene las tabs montadas con `display:none`
  //    (`.tabHidden`, Ventas.module.scss/index.js, keep-alive) — mientras la Pizarra está oculta
  //    detrás de otra tab, `.tableroColumnas` (y el centinela) miden 0 o el navegador ni siquiera
  //    calcula su layout real; si `recalcularAlto` corriera igual (el listener de `resize` de
  //    ventana sigue vivo aunque la tab esté oculta — el componente sigue montado), `top` daría 0 y
  //    el cálculo de arriba daría un alto ENORME y falso. Guard: `el.offsetParent === null` (un
  //    elemento con algún ancestro `display:none` SIEMPRE tiene `offsetParent` nulo) — si es así,
  //    `recalcularAlto` NO mide ni escribe nada, y tampoco toca `alturaAplicadaRef` (para que, al
  //    volver a ser visible, la comparación de abajo sea contra el último alto BUENO, no contra
  //    basura). Al volver a la Pizarra, el centinela pasa de 0×0 a su tamaño real — dispara el
  //    observer — `recalcularAlto` corre de nuevo, esta vez con `offsetParent` válido, y remide
  //    bien. Esto es justo lo que producía el salto/loop en el repro exacto de Ignacio (ir a Mapa
  //    de Calor y volver): la transición oculto→visible de `.tableroColumnas` (el elemento
  //    observado ANTES del fix) coincidía con la primera escritura de altura del mismo tick.
  //
  // Además (parte del mismo fix): la medición real corre DENTRO de `requestAnimationFrame`
  // (desacopla la escritura del ciclo de entrega de notificaciones del observer — técnica
  // estándar para "el observer necesita resizear algo") y el guard de "no reescribir" pasa de
  // igualdad exacta a tolerancia de 1px (`Math.abs(...) <= 1`) — dos valores que se alternan por
  // redondeo (ej. 842 vs 843) ya no quedan rebotando para siempre.
  //
  // PROHIBIDO volver a observar `.tableroColumnas` (o cualquier ancestro cuyo alto dependa del
  // tablero, como `.tableroWrapper` — su alto es automático = suma de sus hijos, así que
  // observarlo es el MISMO lazo) "para simplificar" — reintroduce el error del síntoma 1.
  const tableroColumnasRef = useRef(null);
  const arribaDelTableroRef = useRef(null);
  const alturaAplicadaRef = useRef(null);
  const rafIdRef = useRef(null);
  useLayoutEffect(() => {
    const el = tableroColumnasRef.current;
    if (!el) return undefined;

    const MARGEN_INFERIOR = 15;
    const MARGEN_SEGURIDAD = 2;

    const medirYAplicar = () => {
      // Tab oculta (keep-alive, display:none) — medir acá daría basura (top=0 → alto gigante) o
      // cero. Se saltea SIN tocar alturaAplicadaRef, para comparar contra un valor todavía válido
      // en cuanto vuelva a ser visible.
      if (el.offsetParent === null) return;
      const top = el.getBoundingClientRect().top;
      const nuevoAlto = Math.max(0, Math.round(window.innerHeight - top - MARGEN_INFERIOR - MARGEN_SEGURIDAD));
      if (alturaAplicadaRef.current !== null && Math.abs(alturaAplicadaRef.current - nuevoAlto) <= 1) return;
      alturaAplicadaRef.current = nuevoAlto;
      el.style.height = `${nuevoAlto}px`;
    };

    // rAF desacopla la medición/escritura del ciclo de notificación del ResizeObserver — si dos
    // notificaciones llegan antes del próximo frame, la 2ª cancela el rAF pendiente de la 1ª
    // (nunca se acumulan escrituras redundantes).
    const recalcularAlto = () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        medirYAplicar();
      });
    };

    recalcularAlto();

    // Observa lo de ARRIBA del tablero (nunca el tablero mismo — ver el bloque de comentarios
    // arriba) + el resize de ventana.
    const observer = new ResizeObserver(recalcularAlto);
    if (arribaDelTableroRef.current) observer.observe(arribaDelTableroRef.current);
    window.addEventListener("resize", recalcularAlto);
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      observer.disconnect();
      window.removeEventListener("resize", recalcularAlto);
    };
  }, [loading]);

  const aniosDisponibles = useMemo(() => getAniosCarga(), []);

  // Estructura calcada de VentasDetalle.filtersContent (dos .filterRow de 32px fijos, sin
  // flex-wrap, max-width 1100px) — una tab nunca define su propio layout de barra. 2ª pasada
  // (2026-09-22, punto 1/2): dos filas de 4 controles reales (antes 6+2 con divs de relleno) y
  // "Ver en dólares" pasa a integrarse al final de la fila 2 (antes flotaba afuera, en un wrapper
  // con estilos inline aparte) — ya no hace falta el contenedor externo que separaba ambos
  // bloques, filtersContainer es ahora el único hijo. El checkbox tiene ancho FIJO propio
  // (.filterCheckboxSlot, no flex:1 1 0 como los controles) — la fila 1 lleva un 5º hijo spacer
  // del mismo ancho fijo para que ambas filas midan 5 "columnas" iguales y los controles reales
  // (4 y 4) sigan alineados entre sí verticalmente.
  const filtersContent = (
    <div className={styles.filtersContainer}>
      {/* Fila 1: Mes · Año · Empresa · Producto (4 controles) */}
      <div className={styles.filterRow}>
        <div>
          {/* Multi-mes (2026-09-22): MISMO patrón que VentasDetalle "Mes cierre" — Dropdown de
              react-bootstrap con checkboxes, no un select ni un multiselect propio. */}
          <Dropdown className={styles.mesDropdown}>
            <Dropdown.Toggle
              variant="white border"
              id="dropdown-pizarra-mes"
              className={`form-control text-start ${styles.mesAnoSelect} ${filtroMes.length > 0 ? styles.activeFilter : ""}`}
            >
              {filtroMes.length === 0
                ? "Mes"
                : filtroMes.length >= 4
                  ? "Varios"
                  : filtroMes.map((mes) => MESES_LETRAS[parseInt(mes, 10) - 1]).join(", ")}
            </Dropdown.Toggle>
            <Dropdown.Menu>{renderMesOptions()}</Dropdown.Menu>
          </Dropdown>
        </div>
        <div>
          <select
            className={`form-control ${styles.mesAnoSelect}`}
            value={filtroAno}
            onChange={(e) => setFiltroAno(e.target.value)}
          >
            {aniosDisponibles.map((ano) => (
              <option key={ano} value={String(ano)}>
                {ano}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            className={`form-control ${filtroEmpresa ? styles.activeFilter : ""}`}
            value={filtroEmpresa}
            onChange={(e) => onChangeEmpresa(e.target.value)}
            disabled={empresaDisabled}
            style={empresaDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <option value="">{empresaDisabled ? empresasDeProducto[0] : "Empresa"}</option>
            {empresasDeProducto.map((empresa) => (
              <option key={empresa} value={empresa}>
                {empresa}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            className={`form-control ${filtroProducto ? styles.activeFilter : ""}`}
            value={filtroProducto}
            onChange={(e) => onChangeProducto(e.target.value)}
            disabled={productoDisabled}
            style={productoDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <option value="">{productoDisabled ? productosDeEmpresa[0] : "Producto"}</option>
            {productosDeEmpresa.map((producto) => (
              <option key={producto} value={producto}>
                {producto}
              </option>
            ))}
          </select>
        </div>
        {/* "Ver en dólares" (2026-09-22, enmienda "checkboxes apilados" — antes vivía junto con
            "Mover" en la fila 2, lado a lado; ahora cada fila lleva UN solo checkbox en su propio
            5º slot, alineados verticalmente uno arriba del otro, mismo ancho fijo que el de la
            fila 2 — ver .filterCheckboxSlot). */}
        <label className={`${styles.usdToggle} ${styles.filterCheckboxSlot}`}>
          Ver en dólares
          <input
            type="checkbox"
            checked={viewInUSD}
            onChange={(e) => setViewInUSD(e.target.checked)}
            disabled={!ratesLoaded}
          />
        </label>
      </div>
      {/* Fila 2: Categoría · Subcategoría · Agencia · Ejecutivo (4 controles, CategoriaSubcategoriaFilter
          aporta los 2 primeros) + "Ver en dólares" al final, con el mismo ancho fijo que el
          spacer de la fila 1. */}
      <div className={styles.filterRow}>
        <CategoriaSubcategoriaFilter
          categorias={categorias}
          subcategorias={subcategorias}
          filtroCategoria={filtroCategoria}
          filtroSubcategoria={filtroSubcategoria}
          onCategoriaChange={setFiltroCategoria}
          onSubcategoriaChange={setFiltroSubcategoria}
          className="form-control"
          activeClass={styles.activeFilter}
        />
        <div>
          <AgenciasDropdown
            value={filtroIntermediario}
            onChange={(e) => setFiltroIntermediario(e.target.value)}
            intermediarios={intermediarios}
            placeholder="Agencia"
            className={`form-control ${filtroIntermediario ? styles.activeFilter : ""}`}
          />
        </div>
        <div>
          <select
            className={`form-control ${filtroEjecutivo ? styles.activeFilter : ""}`}
            value={filtroEjecutivo}
            onChange={(e) => setFiltroEjecutivo(e.target.value)}
          >
            <option value="">Ejecutivo</option>
            {ejecutivoOptionsNodes}
          </select>
        </div>
        {/* "Mover" (2026-09-22, enmienda "checkboxes apilados"): mismo slot de ancho fijo que
            "Ver en dólares" en la fila 1 — arriba/abajo entre las dos filas, alineados con ellas,
            sin agrandar el alto de ninguna (cada uno sigue dentro de los 32px de su propia fila).
            Apagado por defecto — con el modo apagado, la Pizarra se comporta exactamente como
            antes (nada arrastrable, badge ×N sin cambios). */}
        <label className={`${styles.usdToggle} ${styles.filterCheckboxSlot}`}>
          Mover
          <input type="checkbox" checked={modoMover} onChange={(e) => setModoMover(e.target.checked)} />
        </label>
      </div>
    </div>
  );

  // Header del shell (10.188/2026-09-21): hook incondicional, corre en TODO render — la Pizarra
  // no tiene ningún early return antes de este punto. Fuera del hub (sin tabKey), fallback
  // standalone con <Header> propio, igual que el resto de las tabs.
  useHubHeaderFilters(tabKey, filtersContent);
  const headerElement = tabKey ? null : renderHeader ? (
    renderHeader(filtersContent)
  ) : (
    <Header expandChildren>{filtersContent}</Header>
  );

  return (
    // Fragment (2026-09-22, confirmación de cierre a 100%): el modal necesita ser HERMANO del
    // marco de página, no un hijo más adentro — reactstrap Modal ya se porta a document.body por
    // su cuenta, la posición en este árbol solo importa para que exista como JSX. Indentación del
    // resto del árbol sin re-anidar a mano (prettier la reacomoda en el build, ver 10.126).
    <>
      {/* Marco de página (10.251/regresión 2026-09-21): con tabKey el marco de 15px ya lo aporta el
        shell (Ventas.module.scss .wrapper) — acá se anula el propio para no duplicarlo. Standalone
        (sin tabKey) conserva su padding:15px de siempre. */}
      <div style={{ padding: tabKey ? 0 : 15 }}>
        {headerElement}
        {/* min-height:100vh (fix scroll espurio 2026-09-22): mismo mecanismo que el padding de
          arriba — solo tiene sentido standalone (la Pizarra es la página entera); dentro del hub
          se suma al header+padding del shell y sobra alto. */}
        <div className={styles.wrapper} style={{ minHeight: tabKey ? 0 : "100vh" }}>
          {loading ? (
            <div style={{ minHeight: 300 }}>
              <Loader />
            </div>
          ) : (
            // Tablero v3 (2026-09-22, docs/decisiones/2026-09-22-pizarra-rediseno-v3-tarjetas.md —
            // fuente de este diseño, validada por Ignacio): la columna deja de ser <table> y pasa a
            // ser un CONTENEDOR con tarjetas. Colores lavados de Bootstrap 5.3 (subtle/emphasis) por
            // variables CSS del módulo, vía data-estatus — no hexas inventados, no clases bg-*. Barra
            // de pipeline + cabezal sin % + agrupación ×N: docs/decisiones/2026-09-22-pizarra-barra-
            // cabezal-agrupacion.md.
            <div className={styles.tableroWrapper}>
              {/* Centinela SIEMPRE montado (2026-09-22, fix loop ResizeObserver — ver el efecto de
                alto fijo más abajo y docs/modulos/pizarra-ventas.md): el observer que dispara el
                recálculo de alto observa ESTE div, nunca `.tableroColumnas` — acá adentro vive todo
                lo que puede cambiar el `top` del tablero por razones que el propio efecto no causa
                (aparece/desaparece la barra de pipeline). Sin className propio a propósito: un
                <div> de bloque sin padding/borde no cambia el layout de sus hijos, los márgenes de
                `.pipelineFranjaTotal`/`.pipelineBarra` siguen colapsando igual que si fueran
                hijos directos de `.tableroWrapper` — cero diferencia visual. */}
              <div ref={arribaDelTableroRef}>
                {hayPipeline && (
                  <>
                    <div className={styles.pipelineFranjaTotal}>
                      {viewInUSD ? formatAmount(totalPipeline, "USD") : formatMonedaLocal(totalPipeline)}
                    </div>
                    <div className={styles.pipelineBarra}>
                      {columnas.map(({ estatus, montoTotal }) => {
                        if (montoTotal <= 0) return null;
                        const pct = (montoTotal / totalPipeline) * 100;
                        const montoFormateado = viewInUSD
                          ? formatAmount(montoTotal, "USD")
                          : formatMonedaLocal(montoTotal);
                        const title = `${estatus} — ${montoFormateado} — ${Math.round(pct)}%`;
                        return (
                          <div
                            key={estatus}
                            className={styles.pipelineSegmento}
                            data-estatus={estatus}
                            style={{ flex: `0 0 ${pct}%`, ...PIPELINE_SEGMENTO_ESTILO[estatus] }}
                            title={title}
                          >
                            {pct >= 6 && <span>{Math.round(pct)}%</span>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              {/* Festejo (2026-09-22): overlay SOBRE el tablero, envuelto junto con .tableroColumnas
                en .tableroColumnasContenedor (position:relative propio — sin esto, el overlay
                position:absolute se posicionaría contra el ancestro posicionado más cercano que
                encuentre, casi nunca este). No toca el ref/alto de .tableroColumnas: es un
                HERMANO, nunca un padre con tamaño propio que pudiera confundir al observer del
                efecto de alto fijo (ver "por qué el observer no puede observar el propio
                tablero" más abajo en la ficha — el mismo cuidado aplica acá, por las dudas). */}
              <div className={styles.tableroColumnasContenedor}>
                {festejoActivo && (
                  <div className={styles.festejoOverlay} aria-hidden="true">
                    {FESTEJO_CONFETI.map((pieza, i) => (
                      <span
                        key={i}
                        className={styles.confetti}
                        style={{ left: `${pieza.left}%`, animationDelay: `${pieza.delay}s`, background: pieza.color }}
                      />
                    ))}
                  </div>
                )}
                <div className={styles.tableroColumnas} ref={tableroColumnasRef}>
                  {columnas.map(
                    ({ estatus, montoTotal, propuestas, clientesDistintos, etiquetas, alternativasPorCliente }) => (
                      <div
                        className={styles.columna}
                        data-estatus={estatus}
                        data-vacio={etiquetas.length === 0 ? "true" : undefined}
                        data-dragover={modoMover && dropTarget === estatus ? "true" : undefined}
                        key={estatus}
                        {...dragZoneProps(estatus)}
                      >
                        <div className={styles.cabezal}>
                          <div className={styles.cabezalCelda}>
                            <div className={styles.cabezalNumero}>{propuestas}</div>
                            <div className={styles.cabezalEtiqueta}>{getEtiquetaPropuestas(estatus)}</div>
                          </div>
                          <div className={styles.cabezalCelda}>
                            <div className={styles.cabezalNumero}>{clientesDistintos}</div>
                            <div className={styles.cabezalEtiqueta}>Clientes</div>
                          </div>
                          <div className={`${styles.cabezalCelda} ${styles.cabezalCeldaMonto}`}>
                            <div className={`${styles.cabezalNumero} ${styles.cabezalNumeroMonto}`}>
                              {viewInUSD ? formatAmount(montoTotal, "USD") : formatMonedaLocal(montoTotal)}
                            </div>
                            <div className={styles.cabezalEtiqueta}>
                              {getEtiquetaMonto(estatus)}
                              {estatus === "100%" && ventana100Mes && ` · ${ventana100Label}`}
                            </div>
                          </div>
                        </div>
                        <div className={styles.cuerpo}>
                          {etiquetas.length === 0 ? (
                            <div className={styles.vacio}>Sin propuestas</div>
                          ) : (
                            etiquetas.map((op) => {
                              const cliente = clienteById[op.client_id];
                              const empresa = cliente?.empresa || op.cliente || "Cliente";
                              const marca = cliente?.producto || "";
                              // Punto 3 (2ª pasada): "TRANSBANK | TRANSBANK" no aporta nada — si marca y
                              // empresa son la misma cosa (normalizada: trim + mayúsculas), se muestra
                              // solo empresa. Una sola fuente de verdad (marcaDistinta), reusada por la
                              // tarjeta Y por el title del tooltip.
                              const marcaDistinta = marca !== "" && normalizarTexto(marca) !== normalizarTexto(empresa);
                              const agenciaNombre = intermediaryMap[clienteIntermediarioMap[op.client_id]] || "";
                              const montoOp = formatMontoOp(op);
                              // Agencia y campaña NO se muestran en la tarjeta — siguen en el title del
                              // tooltip, sin cambios.
                              const detalleTooltip = [op.campana, agenciaNombre].filter(Boolean).join(" · ");
                              const title = `${empresa}${marcaDistinta ? ` | ${marca}` : ""}${detalleTooltip ? ` — ${detalleTooltip}` : ""} — ${montoOp}`;

                              // Pastilla de antigüedad / fecha de cierre (punto 5): 100% muestra la
                              // fecha de cierre (sin semáforo, no hay downgrade que vigilar); 50/75/90
                              // muestran días desde COALESCE(updated_at, created_at) — mismo criterio
                              // que el cron downgrade_pipeline_status, vía getDowngradeInfo (única
                              // fuente, reusada — 10.1). sales_btl no está cubierto por el cron: se ve
                              // el mismo conteo de días, pero el color nunca sale de "gray".
                              const downgradeInfo = estatus === "100%" ? null : getDowngradeInfo(op);
                              const celdaAntiguedad =
                                estatus === "100%"
                                  ? formatFechaCierreTarjeta(op)
                                  : downgradeInfo
                                    ? `${downgradeInfo.daysSinceUpdate}d`
                                    : "";
                              const pillColor =
                                estatus !== "100%" && op.type !== "btl" && downgradeInfo
                                  ? downgradeInfo.diasRestantes < 3
                                    ? "red"
                                    : downgradeInfo.diasRestantes < 7
                                      ? "amber"
                                      : "gray"
                                  : "gray";
                              // Tooltip de la columna 100% (punto 2, 4ª pasada): la tarjeta ya no
                              // muestra el año ("11/09"), así que el tooltip es lo único que lo
                              // conserva — formatFechaCierreCompletaTarjeta, no la corta.
                              const tituloCeldaAntiguedad =
                                estatus === "100%"
                                  ? `Fecha de cierre: ${formatFechaCierreCompletaTarjeta(op)}`
                                  : tituloAntiguedad(op, downgradeInfo);

                              // Agrupación ×N (punto 3): todas las propuestas de ESTE cliente en esta
                              // columna. Vacío al 100% por construcción (alternativasPorCliente no se
                              // pobló ahí) → tieneAlternativas siempre false, sin badge ni despliegue.
                              const clientId = op.client_id || op.cliente;
                              const alternativas = alternativasPorCliente.get(clientId) || [];
                              const tieneAlternativas = alternativas.length > 1;
                              const grupoKey = `${estatus}:${clientId}`;
                              const abierto = tieneAlternativas && gruposAbiertos.has(grupoKey);
                              // Orden ascendente por el MISMO criterio que elige la ganadora (10.272,
                              // getValorSeleccion, siempre equivalente-USD) — así "la que cuenta" queda
                              // primera por definición, sin importar la moneda mostrada.
                              const hijasOrdenadas = tieneAlternativas
                                ? [...alternativas].sort((a, b) => getValorSeleccion(a) - getValorSeleccion(b))
                                : [];

                              // Modo Mover: `alternativas` YA ES el grupo completo del cliente en esta
                              // columna (se pobló para TODOS los clientes, no solo los que tienen ×N —
                              // ver `columnas` arriba), así que reusarlo acá evita recalcular nada.
                              // El 100% SÍ es arrastrable (habilitado 2026-09-22, "el mejor momento del
                              // mes") pero con semántica DISTINTA: no hay grupo (`alternativasPorCliente`
                              // sigue vacío ahí a propósito, cada fila es una venta cerrada distinta —
                              // 10.272 §4), así que lo que se arrastra es SOLO esta propuesta, nunca un
                              // grupo por cliente. `grupoOrigenTarjeta` refleja eso: `[op]` en el 100%,
                              // `alternativas` en cualquier otra columna.
                              const grupoOrigenTarjeta = estatus === "100%" ? [op] : alternativas;
                              const grupoEditable = grupoOrigenTarjeta.every((o) => isEditableByDate(o.created_at));
                              const puedeArrastrar = modoMover && grupoEditable && !moviendo;
                              const tarjetaTitle =
                                modoMover && !grupoEditable
                                  ? `${title} — no se puede mover: hay una propuesta no editable en este grupo`
                                  : title;

                              return (
                                <div key={claveOportunidad(op)} className={styles.grupoCliente}>
                                  <div
                                    className={[
                                      styles.tarjeta,
                                      abierto ? styles.tarjetaAbierta : "",
                                      puedeArrastrar ? styles.tarjetaArrastrable : "",
                                      modoMover && !grupoEditable ? styles.tarjetaNoArrastrable : "",
                                      draggingKey === claveOportunidad(op) ? styles.tarjetaArrastrando : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    title={tarjetaTitle}
                                    draggable={puedeArrastrar}
                                    onDragStart={(e) => {
                                      if (!puedeArrastrar) return;
                                      e.dataTransfer.effectAllowed = "move";
                                      e.dataTransfer.setData("text/plain", String(clientId));
                                      // `opClave` identifica la propuesta EXACTA tomada — imprescindible
                                      // para el origen "100%" (ahí clientId no alcanza: un mismo cliente
                                      // puede tener varias ventas cerradas distintas en esa columna, y
                                      // solo se mueve ESTA, nunca "todas las de ese cliente al 100%").
                                      setDragOrigen({ estatus, clientId, opClave: claveOportunidad(op) });
                                      setDraggingKey(claveOportunidad(op));
                                    }}
                                    onDragEnd={() => {
                                      setDragOrigen(null);
                                      setDropTarget(null);
                                      setDraggingKey(null);
                                    }}
                                  >
                                    {/* Punto 1 (4ª pasada, 2026-09-26): "SAMSUNG · CÓMPUTOS" — la
                                      empresa vuelve a la tarjeta (la marca sola perdía contexto),
                                      pero nunca se trunca: solo `.tarjetaClienteMarca` lleva
                                      ellipsis, `.tarjetaClienteEmpresa`/separador son
                                      flex-shrink:0. Sin marca (o marca=empresa, marcaDistinta
                                      false), va sola la empresa, sin separador colgando. */}
                                    <span className={styles.tarjetaCliente}>
                                      <span className={styles.tarjetaClienteEmpresa}>{empresa}</span>
                                      {marcaDistinta && (
                                        <>
                                          <span className={styles.tarjetaClienteSeparador}>·</span>
                                          <span className={styles.tarjetaClienteMarca}>{marca}</span>
                                        </>
                                      )}
                                    </span>
                                    {/* Slot SIEMPRE presente (punto 4) — vacío sin agrupación, pero
                                      ocupa su columna de la grilla igual: sin esto, la pastilla de
                                      abajo se corre a la posición del badge en cuanto una tarjeta no
                                      tiene ×N, rompiendo la lectura de tabla entre tarjetas. */}
                                    <span className={styles.tarjetaBadgeSlot}>
                                      {tieneAlternativas && (
                                        <button
                                          type="button"
                                          className={styles.badgeAlternativas}
                                          draggable={false}
                                          onDragStart={(e) => e.stopPropagation()}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleGrupo(grupoKey);
                                          }}
                                        >
                                          ×{alternativas.length}
                                          {abierto ? " ▾" : ""}
                                        </button>
                                      )}
                                    </span>
                                    <span
                                      className={
                                        estatus === "100%" ? styles.tarjetaFechaCierre : styles.tarjetaAntiguedad
                                      }
                                      data-color={estatus === "100%" ? undefined : pillColor}
                                      title={tituloCeldaAntiguedad}
                                    >
                                      {celdaAntiguedad}
                                    </span>
                                    <span className={styles.tarjetaMonto}>{montoOp}</span>
                                  </div>
                                  {abierto && (
                                    <div className={styles.hijas}>
                                      {hijasOrdenadas.map((hija) => {
                                        const esLaQueCuenta = claveOportunidad(hija) === claveOportunidad(op);
                                        const agenciaHija =
                                          intermediaryMap[clienteIntermediarioMap[hija.client_id]] || "";
                                        const detalleHija = [hija.campana, agenciaHija].filter(Boolean).join(" · ");
                                        const montoHija = formatMontoOp(hija);
                                        return (
                                          <div
                                            key={claveOportunidad(hija)}
                                            className={`${styles.hijaFila} ${esLaQueCuenta ? styles.hijaQueCuenta : ""}`}
                                            title={`${detalleHija || "Sin campaña"} — ${montoHija}`}
                                          >
                                            <span className={styles.hijaDetalle}>{detalleHija}</span>
                                            <span className={styles.hijaMonto}>
                                              {montoHija}
                                              {esLaQueCuenta ? "." : ""}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
                {/* Zona de descarte — "Se cayó · 0%" (punto 1, 2026-09-26): SOLO con el modo
                  Mover prendido, apagado no existe. NO es un tacho de basura (sin ícono de
                  tacho) — la flecha hacia abajo es el único ícono; la propuesta no se borra, cae
                  a 0% y queda de registro (ver docs/modulos/pizarra-ventas.md). Hermana del
                  festejo dentro de .tableroColumnasContenedor (mismo position:relative), pero
                  SIN pointer-events:none — necesita recibir los eventos de drop. Reusa
                  `dragZoneProps` con el sentinela "0%" (nunca una columna real, ver el
                  comentario de arriba). `data-dragover` es el único momento en que la caja
                  cambia de tamaño (pedido explícito) — mismo mecanismo que ya resalta las 4
                  columnas (`dropTarget === estatus`), acá comparado contra "0%". */}
                {modoMover && (
                  <div
                    className={styles.zonaDescarte}
                    data-dragover={dropTarget === "0%" ? "true" : undefined}
                    title="Soltá acá una propuesta para marcarla como caída — pasa a 0%, no se borra, queda de registro"
                    {...dragZoneProps("0%")}
                  >
                    <span className={styles.zonaDescarteFlecha} aria-hidden="true">
                      ↓
                    </span>
                    <span>Se cayó · 0%</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmarCierre100Modal
        isOpen={!!confirmacion100}
        nombreCliente={
          confirmacion100
            ? nombreCompletoCliente(clienteById[confirmacion100.origen.clientId], confirmacion100.grupo[0]?.cliente)
            : ""
        }
        items={confirmacion100?.items || []}
        ganadoraDefaultClave={confirmacion100?.ganadoraDefaultClave}
        onCancel={() => setConfirmacion100(null)}
        onConfirm={handleConfirmarCierre100}
      />
      <ConfirmarCaidaModal
        isOpen={!!confirmacionCaida}
        nombreCliente={confirmacionCaida?.nombreCliente || ""}
        campana={confirmacionCaida?.campana || ""}
        monto={confirmacionCaida?.monto || ""}
        onCancel={() => setConfirmacionCaida(null)}
        onConfirm={handleConfirmarCaida0}
      />
    </>
  );
};

export default Pizarra;
